/*
  What the "Save X%" badge on a plan card should read once the customer's
  signup promo code is taken into account.

  The badge has always meant one thing: how much cheaper this plan is than
  paying monthly for the same stretch of time. The backend computes it as
  (total_price_no_discount - unit_amount) / total_price_no_discount, and the
  card prints "Au lieu de <total_price_no_discount>" directly above it. So the
  promo is folded in by lowering the price and asking that same question again,
  rather than by adding two percentages together.

  The two are not the same. A 1-year plan at 197 EUR against 238.80 EUR of
  monthly payments saves 17.5%. Add a 10% code and the customer pays 177.30,
  which is 25.75% below 238.80 -- not 27.5%. Addition would overstate the
  saving and would not reconcile with the amount charged at checkout.

  Everything here is in Stripe's minor units (cents), with one exception that
  has bitten before: `amount_off` on a coupon arrives in MAJOR units. ChoosePlan
  subtracts it straight from a subtotal it has already divided by 100. So it is
  multiplied by 100 here before meeting a price.
*/

/*
  Whether the coupon is usable on this product at all.

  `applies_to` is absent on a coupon that covers everything. When present it is
  a list, and its shape depends on which endpoint answered: /referral/validate
  maps it to {metadata, name, id} objects, while the pending and validate
  endpoints under /user/pay return bare product-id strings. Both are accepted
  rather than normalised at the call sites, which is where the next shape would
  otherwise be missed.
*/
export const couponAppliesToProduct = (coupon, product) => {
  const applies = coupon?.applies_to;
  if (!applies || !applies.length) return true;
  const id = product?.id;
  if (!id) return false;
  return applies.some((entry) => (typeof entry === "string" ? entry : entry?.id) === id);
};

/*
  How much the coupon takes off this price, in cents.

  A percentage scales the price. A fixed sum is looked up per currency --
  `amount_off_currencies` is keyed by lowercase currency and already converted,
  while `amount_off` is only correct for the currency the coupon was fetched
  in, so it is used only when that currency matches. A sum larger than the
  price is clamped: a plan is never worth less than nothing.
*/
export const promoOffCents = (coupon, currency, priceCents) => {
  if (!coupon || !Number.isFinite(priceCents) || priceCents <= 0) return 0;

  const percent = Number(coupon.percent_off);
  if (Number.isFinite(percent) && percent > 0) {
    return Math.min((priceCents * percent) / 100, priceCents);
  }

  const key = String(currency || "").toLowerCase();
  let major = coupon?.amount_off_currencies?.[key];
  if (major === undefined || major === null) {
    // Only trust the flat `amount_off` when it is quoted in this currency.
    if (key && key === String(coupon?.currency || "").toLowerCase()) {
      major = coupon?.amount_off;
    }
  }
  const amount = Number(major);
  if (!Number.isFinite(amount) || amount <= 0) return 0;

  return Math.min(amount * 100, priceCents);
};

/*
  The number the badge prints, or null when there is nothing to boast about.

  Returns the plan's own saving untouched when there is no coupon, so a card
  with no promo renders exactly as it did before: the arithmetic below is the
  same one the backend already ran.

  The monthly plan and the lifetime plan carry no `total_price_no_discount` --
  monthly IS the baseline, and lifetime is not a multiple of it. They fall back
  to their own price as the baseline, which means they show nothing without a
  coupon (0%) and show the promo's own value with one. That is the honest
  answer for both.
*/
export const combinedDiscountPercent = (product, currency, coupon) => {
  const options = product?.default_price?.currency_options?.[currency];
  if (!options) return null;

  const priceCents = Number(options.unit_amount);
  if (!Number.isFinite(priceCents) || priceCents <= 0) return null;

  const baseline = Number(options.total_price_no_discount) || priceCents;
  if (!Number.isFinite(baseline) || baseline <= 0) return null;

  const usable = couponAppliesToProduct(coupon, product) ? coupon : null;
  const finalPrice = Math.max(priceCents - promoOffCents(usable, currency, priceCents), 0);

  const percent = ((baseline - finalPrice) / baseline) * 100;
  if (!Number.isFinite(percent) || percent <= 0) return null;
  return percent;
};

/*
  What the customer actually pays for this plan, in cents, once the coupon is
  applied -- the total charged, not a per-year slice.

  The badge and the printed price have to come from the same arithmetic. A card
  reading "197 EUR, instead of 238.80, save 25%" is self-contradictory: those
  two figures are 17.5% apart. So whatever lowers the percentage has to lower
  the price on the card by the same amount.

  Returns null when there is no price to show, and the unchanged price when no
  coupon applies.
*/
export const discountedPriceCents = (product, currency, coupon) => {
  const options = product?.default_price?.currency_options?.[currency];
  const priceCents = Number(options?.unit_amount);
  if (!Number.isFinite(priceCents) || priceCents <= 0) return null;

  const usable = couponAppliesToProduct(coupon, product) ? coupon : null;
  return Math.max(priceCents - promoOffCents(usable, currency, priceCents), 0);
};

/*
  The same figure as the card's big number, which is not always a per-year one.

  The card prints `yearly_price` when the backend supplied one and the raw
  price otherwise, so this scales by whatever ratio those two already stand in
  rather than dividing by `years`. That matters for the lifetime plan: its
  `years` is 99 and it has no `yearly_price`, so dividing would turn 3997 into
  a meaningless 40. The monthly plan has no `yearly_price` either, and its
  total is already its per-period price.
*/
export const discountedPerYearCents = (product, currency, coupon) => {
  const total = discountedPriceCents(product, currency, coupon);
  if (total === null) return null;

  const options = product?.default_price?.currency_options?.[currency];
  const unit = Number(options?.unit_amount);
  const yearly = Number(options?.yearly_price);
  if (!Number.isFinite(yearly) || yearly <= 0) return total;
  if (!Number.isFinite(unit) || unit <= 0) return total;

  return total * (yearly / unit);
};

/*
  What the card's big number reads WITHOUT any coupon -- `yearly_price` when
  there is one, the plain price otherwise. Used as the struck-through figure on
  plans that have no monthly-equivalent baseline to show instead.
*/
export const listPriceCents = (product, currency) => {
  const options = product?.default_price?.currency_options?.[currency];
  const yearly = Number(options?.yearly_price);
  if (Number.isFinite(yearly) && yearly > 0) return yearly;
  const unit = Number(options?.unit_amount);
  return Number.isFinite(unit) && unit > 0 ? unit : null;
};

/*
  Cents to the number the card prints. Two decimals at most, and no trailing
  zeros, so 19700 stays "197" while 17730 becomes "177.3" -- matching how
  prices already read on this page ("19.9", "238.8").
*/
export const formatAmount = (cents) => {
  if (cents === null || !Number.isFinite(cents)) return null;
  return Number((cents / 100).toFixed(2));
};

/*
  Whether this card's badge is being lifted by the coupon, as opposed to
  showing the plan's ordinary saving. Lets the card mark the difference without
  recomputing anything.
*/
export const isPromoBoosted = (product, currency, coupon) => {
  if (!coupon || !couponAppliesToProduct(coupon, product)) return false;
  const options = product?.default_price?.currency_options?.[currency];
  const priceCents = Number(options?.unit_amount);
  return promoOffCents(coupon, currency, priceCents) > 0;
};
